import { useRef, ReactNode, memo } from 'react';
import clsx from 'clsx';
import './customFileToolbar.scss';
import Api from '@/api/api';
import { useMeetingStore } from '@/stores/meeting.ts';
import { useSend } from '@/components/SendProvider';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import {
  useEditor,
  TldrawUiToolbarButton,
  TldrawUiToolbar,
  useValue,
  MobileStylePanel,
  useTranslation,
  useBreakpoint,
  useReadonly,
  usePassThroughWheelEvents,
  TldrawUiOrientationProvider,
  PORTRAIT_BREAKPOINT,
  ToggleToolLockedButton,
  OverflowingToolbar,
  DefaultToolbarContent,
  useTldrawUiComponents
} from 'tldraw';
import { DocProps } from '@/types/api';
import { useBoardPdfStore } from '@/stores/boardPdf';

interface StudentFileUpdateMessage {
  action: 'student-file-add' | 'student-file-remove';
  file: DocProps;
}

interface MyToolbarProps {
  children?: ReactNode;
  orientation?: 'horizontal' | 'vertical';
  minItems?: number;
  minSizePx?: number;
  maxItems?: number;
  maxSizePx?: number;
}

function CustomFileToolbar() {
  const width = useWindowWidth();
  return (
    <div className="PdfEditor__toolbar">
      <div className="toolbar">
        <MyToolbar />
        {width >= 1440 && <CustomToolbarButton />}
      </div>
      {width <= 1440 && <CustomToolbarButton />}
    </div>
  );
}

const CustomMobileStylePanel = memo(function CustomMobileStylePanel() {
  return (
    <div className="tlui-main-toolbar__tools tlui-main-toolbar__mobile-style-panel">
      <MobileStylePanel />
    </div>
  );
});

const MyToolbar = memo(function MyToolbar({
  children,
  orientation = 'horizontal',
  minItems = 4,
  minSizePx = 310,
  maxItems = 8,
  maxSizePx = 470
}: MyToolbarProps) {
  const editor = useEditor();
  const msg = useTranslation();
  const breakpoint = useBreakpoint();
  const isReadonlyMode = useReadonly();
  const activeToolId = useValue('current tool id', () => editor.getCurrentToolId(), [editor]);

  const ref = useRef<HTMLDivElement>(null);
  usePassThroughWheelEvents(ref);

  const { ActionsMenu, QuickActions } = useTldrawUiComponents();

  const showQuickActions =
    editor.options.actionShortcutsLocation === 'menu'
      ? false
      : editor.options.actionShortcutsLocation === 'toolbar'
        ? true
        : breakpoint < PORTRAIT_BREAKPOINT.TABLET;

  const width = useWindowWidth();
  return (
    <TldrawUiOrientationProvider
      orientation={orientation}
      tooltipSide={orientation === 'horizontal' ? 'top' : 'right'}
    >
      <div ref={ref} className={clsx('tlui-main-toolbar', `tlui-main-toolbar--${orientation}`)}>
        <div className="tlui-main-toolbar__inner">
          <div className="tlui-main-toolbar__left">
            {!isReadonlyMode && (
              <div className="tlui-main-toolbar__extras">
                {showQuickActions && (
                  <TldrawUiToolbar
                    orientation={orientation}
                    className="tlui-main-toolbar__extras__controls"
                    label={msg('actions-menu.title')}
                  >
                    {QuickActions && <QuickActions />}
                    {ActionsMenu && <ActionsMenu />}
                  </TldrawUiToolbar>
                )}
                <ToggleToolLockedButton activeToolId={activeToolId} />
              </div>
            )}

            <OverflowingToolbar
              orientation={orientation}
              sizingParentClassName="tlui-main-toolbar"
              minItems={minItems}
              maxItems={maxItems}
              minSizePx={minSizePx}
              maxSizePx={maxSizePx}
            >
              {children ?? <DefaultToolbarContent />}
            </OverflowingToolbar>
          </div>

          {width <= 1440 && !isReadonlyMode && <CustomMobileStylePanel />}
        </div>
      </div>
    </TldrawUiOrientationProvider>
  );
});

function CustomToolbarButton() {
  const studentDocs = useBoardPdfStore((state) => state.studentDocs);
  const addStudentDoc = useBoardPdfStore((state) => state.addStudentDoc);
  const removeStudentDoc = useBoardPdfStore((state) => state.removeStudentDoc);
  const activeDoc = useBoardPdfStore((state) => state.activeDoc);
  const setActiveDoc = useBoardPdfStore((state) => state.setActiveDoc);
  const { sendFileToStudents } = useSend();
  const isStudentDoc = activeDoc && studentDocs.some((doc) => doc.id === activeDoc.id);
  const meeting = useMeetingStore((state) => state.meeting);
  const meetingUid = meeting?.uuid;

  const handleStudentDoc = () => {
    if (!activeDoc || !meetingUid) return;
    const updateStudentDocs = async () => {
      if (isStudentDoc) {
        await Api.unshareFileWithStudentInMeeting(meetingUid, activeDoc.id);

        const message: StudentFileUpdateMessage = {
          action: 'student-file-remove',
          file: activeDoc
        };
        sendFileToStudents(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });

        removeStudentDoc(activeDoc.id);
      } else {
        await Api.shareFileWithStudentInMeeting(meetingUid, activeDoc.id);

        const message: StudentFileUpdateMessage = { action: 'student-file-add', file: activeDoc };
        sendFileToStudents(new TextEncoder().encode(JSON.stringify(message)), { reliable: true });

        addStudentDoc(activeDoc);
      }
    };
    updateStudentDocs();
  };

  return (
    <div className="my-toolbar">
      <TldrawUiToolbar className="my-toolbar-buttons" label="new" tooltipSide="top">
        <TldrawUiToolbarButton
          type="tool"
          className="my-toolbar-button tlui-main-toolbar__tools"
          isActive={true}
          disabled={false}
          tooltip="Прекратить показ"
          onClick={() => setActiveDoc({ id: '', path: '' })}
        >
          <i className="pi pi-eye-slash" style={{ color: '#fff' }}></i>
        </TldrawUiToolbarButton>
        <TldrawUiToolbarButton
          type="tool"
          className={clsx(
            'my-toolbar-button',
            'tlui-main-toolbar__tools',
            isStudentDoc ? 'student-doc' : 'not-student-doc'
          )}
          isActive={true}
          disabled={false}
          tooltip={!isStudentDoc ? 'Поделиться с учениками' : 'Удалить у учеников'}
          onClick={handleStudentDoc}
        >
          <i
            className={clsx('pi', isStudentDoc ? 'pi-user-minus' : 'pi-user-plus')}
            style={{ color: '#fff' }}
          ></i>
        </TldrawUiToolbarButton>
      </TldrawUiToolbar>
    </div>
  );
}

export default CustomFileToolbar;
